/**
 * useThreadCategories — the viewer's sections, live (chat-thread experience
 * PR-5, 2026-09-09; built-ins first-class 2026-09-09). Subscribes per
 * signed-in uid and exposes the whole organization surface over the one meta
 * doc: create (folding by name), rename (built-ins too), remove (custom
 * only), move up / down, collapse. Optimistic: the list updates locally at
 * once and the write settles behind it; a failed write is undone by the next
 * snapshot. A stored custom category named like a built-in is folded into it
 * on read (the cleaned list is written back once; the host re-files threads
 * through `foldAliases`).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  saveThreadCategories,
  subscribeThreadCategories,
  type ThreadCategoriesState,
} from '../repositories/threadCategoriesRepository';
import {
  MAX_THREAD_CATEGORIES,
  builtinKindForName,
  customCategories,
  findCategoryByName,
  isBuiltinCategoryId,
  newThreadCategoryId,
  normalizeThreadCategoryName,
  withBuiltinSections,
  type ThreadCategory,
} from '../types/threadCategories';

export type CategoryMoveDirection = 'up' | 'down';

export interface UseThreadCategoriesResult {
  /** Every section in the viewer's order — custom categories and the three built-ins. */
  categories: ThreadCategory[];
  collapsed: ReadonlySet<string>;
  /** Custom ids that fold into a built-in (threads filed under them read as filed under the built-in). */
  foldAliases: Readonly<Record<string, string>>;
  /** Create a category by name — or return the id of the section that name already means (fold). Null = empty name / cap hit. */
  createCategory: (name: string) => Promise<string | null>;
  /** Rename any section (a built-in renamed back to its default label stores no name). False = empty or taken. */
  renameCategory: (id: string, name: string) => Promise<boolean>;
  /** Remove a CUSTOM category (a built-in is refused). The host re-files its threads. */
  removeCategory: (id: string) => Promise<boolean>;
  moveCategory: (id: string, direction: CategoryMoveDirection) => Promise<boolean>;
  setCollapsed: (id: string, collapsed: boolean) => Promise<void>;
}

const EMPTY_STATE: ThreadCategoriesState = { items: [], collapsed: [], foldAliases: {} };

export function useThreadCategories(uid: string | null | undefined): UseThreadCategoriesResult {
  const [state, setState] = useState<ThreadCategoriesState>(EMPTY_STATE);
  const latestRef = useRef<ThreadCategoriesState>(EMPTY_STATE);
  latestRef.current = state;
  const foldWrittenRef = useRef(false);

  useEffect(() => {
    const id = (uid ?? '').trim();
    setState(EMPTY_STATE);
    foldWrittenRef.current = false;
    if (!id) return undefined;
    let unsubscribe: (() => void) | null = null;
    try {
      unsubscribe = subscribeThreadCategories(id, (next) => {
        setState(next);
        // A folded custom section is gone from the list it came from: write
        // the cleaned list back once so every device agrees.
        if (Object.keys(next.foldAliases).length > 0 && !foldWrittenRef.current) {
          foldWrittenRef.current = true;
          saveThreadCategories(id, withBuiltinSections(next.items), next.collapsed).catch(() => undefined);
        }
      });
    } catch {
      unsubscribe = null;
    }
    return () => { unsubscribe?.(); };
  }, [uid]);

  const categories = useMemo(() => withBuiltinSections(state.items), [state.items]);
  const collapsed = useMemo(() => new Set(state.collapsed), [state.collapsed]);

  const persist = useCallback(async (items: ThreadCategory[], collapsed?: string[]) => {
    const id = (uid ?? '').trim();
    const full = withBuiltinSections(items);
    setState((prev) => ({ ...prev, items: full, ...(collapsed ? { collapsed } : {}) }));
    if (!id) return;
    try {
      await saveThreadCategories(id, full, collapsed ?? latestRef.current.collapsed);
    } catch { /* the next snapshot restores the stored list */ }
  }, [uid]);

  const createCategory = useCallback(async (rawName: string): Promise<string | null> => {
    const name = normalizeThreadCategoryName(rawName);
    if (!name) return null;
    const current = withBuiltinSections(latestRef.current.items);
    const existing = findCategoryByName(current, name);
    if (existing) return existing.id;
    if (customCategories(current).length >= MAX_THREAD_CATEGORIES) return null;
    const id = newThreadCategoryId();
    // A new category joins ABOVE the built-ins (custom first — today's rail).
    const firstBuiltin = current.findIndex((c) => !!c.builtin);
    const at = firstBuiltin < 0 ? current.length : firstBuiltin;
    const next = [...current.slice(0, at), { id, name, order: at }, ...current.slice(at)];
    await persist(next);
    return id;
  }, [persist]);

  const renameCategory = useCallback(async (id: string, rawName: string): Promise<boolean> => {
    const name = normalizeThreadCategoryName(rawName);
    if (!name) return false;
    const current = withBuiltinSections(latestRef.current.items);
    const target = current.find((c) => c.id === id);
    if (!target) return false;
    const clash = findCategoryByName(current, name);
    if (clash && clash.id !== id) return false;
    // A built-in renamed to its own default label keeps no name (the label follows the language).
    const backToDefault = !!target.builtin && builtinKindForName(name) === target.builtin;
    await persist(current.map((c) => (c.id === id ? { ...c, name: backToDefault ? '' : name } : c)));
    return true;
  }, [persist]);

  const removeCategory = useCallback(async (id: string): Promise<boolean> => {
    if (isBuiltinCategoryId(id)) return false;
    const current = withBuiltinSections(latestRef.current.items);
    if (!current.some((c) => c.id === id)) return false;
    await persist(current.filter((c) => c.id !== id));
    return true;
  }, [persist]);

  const moveCategory = useCallback(async (id: string, direction: CategoryMoveDirection): Promise<boolean> => {
    const current = withBuiltinSections(latestRef.current.items);
    const index = current.findIndex((c) => c.id === id);
    const target = direction === 'up' ? index - 1 : index + 1;
    if (index < 0 || target < 0 || target >= current.length) return false;
    const next = [...current];
    [next[index], next[target]] = [next[target], next[index]];
    await persist(next);
    return true;
  }, [persist]);

  const setCollapsed = useCallback(async (id: string, collapsed: boolean): Promise<void> => {
    const current = latestRef.current.collapsed.filter((c) => c !== id);
    const next = collapsed ? [...current, id] : current;
    await persist(withBuiltinSections(latestRef.current.items), next);
  }, [persist]);

  return {
    categories,
    collapsed,
    foldAliases: state.foldAliases,
    createCategory,
    renameCategory,
    removeCategory,
    moveCategory,
    setCollapsed,
  };
}
